class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.bundlerPromptShowing = false;
    this.bundlerPromptGeneration = 0;
    this.bundlerFocusReturn = null;
    this.bundlerFocusIsInside = false;

    document.addEventListener('focusin', (event) => {
      this.bundlerFocusIsInside = !!event.target.closest?.('#bndlr-discount-message');
    });

    document.addEventListener('bundler:checkout_prompt_shown', () => {
      const popup = document.getElementById('bndlr-discount-message');
      if (!popup) return;

      const shouldFocus = !this.bundlerPromptShowing || this.bundlerFocusIsInside;
      const focused = document.activeElement;
      const focusReturn = this.activeElement?.isConnected ? this.activeElement : focused;
      if (focusReturn instanceof HTMLElement && focusReturn !== document.body &&
          focusReturn !== this.notification &&
          !this.notification.contains(focusReturn) && !popup.contains(focusReturn)) {
        this.bundlerFocusReturn = focusReturn;
      }

      this.bundlerPromptGeneration += 1;
      this.bundlerPromptShowing = true;
      if (this.notification.classList.contains('active')) this.close();

      const title = popup.querySelector('.bndlr-message-title');
      if (title) {
        title.id = 'bndlr-discount-message-title';
        popup.setAttribute('aria-labelledby', title.id);
      }
      const savings = popup.querySelector('.bndlr-message-discount-value');
      if (savings) {
        savings.id = 'bndlr-discount-message-value';
        popup.setAttribute('aria-describedby', savings.id);
      }
      popup.setAttribute('role', 'dialog');
      popup.setAttribute('aria-modal', 'false');

      for (const selector of ['.bndlr-message-yes', '.bndlr-message-no', '.bndlr-message-close']) {
        const control = popup.querySelector(selector);
        if (!control) continue;
        control.setAttribute('role', 'button');
        control.tabIndex = 0;
        if (selector === '.bndlr-message-close') control.setAttribute('aria-label', 'Close discount message');
      }
      if (shouldFocus) popup.querySelector('.bndlr-message-yes')?.focus({ preventScroll: true });
    });

    document.addEventListener('bundler:total_cart_values', (event) => {
      const itemCount = Number(event.detail?.original_cart_object?.item_count);
      const discount = Number(event.detail?.discount_value);
      const noOffer = Number.isFinite(discount) && discount <= 0 &&
        event.detail?.can_get_free_shipping_discount !== true;
      if (itemCount !== 0 && !(Number.isFinite(itemCount) && noOffer)) return;
      document.getElementById('bndlr-discount-message')?.remove();
      this.bundlerPromptShowing = false;
      this.bundlerPromptGeneration += 1;
    });

    document.addEventListener('click', (event) => {
      if (!event.target.closest?.('.bndlr-message-no, .bndlr-message-close')) return;
      const generation = this.bundlerPromptGeneration;
      window.setTimeout(() => {
        if (this.bundlerPromptGeneration !== generation) return;
        this.bundlerPromptShowing = false;
        const focusTarget = this.bundlerFocusReturn?.isConnected &&
          !this.bundlerFocusReturn.matches(':disabled') ? this.bundlerFocusReturn :
          document.getElementById('cart-icon-bubble');
        focusTarget?.focus({ preventScroll: true });
      }, 1000);
    }, true);

    document.addEventListener('keydown', (event) => {
      const popup = event.target.closest?.('#bndlr-discount-message');
      if (!popup) return;
      if (event.repeat && ['Escape', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        popup.querySelector('.bndlr-message-close')?.click();
        return;
      }
      const control = event.target.closest('.bndlr-message-yes, .bndlr-message-no, .bndlr-message-close');
      if (control && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        if (control.matches('.bndlr-message-yes.bndlr-loading')) return;
        control.click();
      }
    });

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    if (this.bundlerPromptShowing) return;

    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        if (!this.notification.classList.contains('active')) return;
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
